import { useParams } from "react-router-dom";
import { WorkflowForm } from "../../../components/workflow/WorkflowForm";
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { workflowService } from "@/services/features/WorkflowService";
import LoaderComponent from "@/components/common/LoaderComponent";
import { toast } from "@/hooks/use-toast";
import { useProgramTypes } from "@/hooks/workflow/useProgramTypes";
import { useWorkflowsList } from "@/hooks/workflow/useWorkflowsList";
import { useTasksList } from "@/hooks/workflow/useTasksList";

const EditWorkflow = () => {
  const { id } = useParams<{ id: string }>();
  const { user, token } = useAuth();
  const [workflowData, setWorkflowData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Fetch dropdown data to map IDs to names
  const { programTypes, loading: loadingPrograms } = useProgramTypes();
  const { workflows, loading: loadingWorkflows } = useWorkflowsList();
  const { tasks, loading: loadingTasks } = useTasksList();

  // Transform API response to form format
  const transformApiDataToFormData = (apiData: any) => {
    const workflow = apiData.workflow;
    
    // Map program type IDs to names
    const applicablePrograms = workflow.programTypes?.map((pt: any) => {
      const program = programTypes.find(p => p.program_type_id === pt.programTypeId);
      return program?.program_type || "";
    }).filter(Boolean) || [];

    // Map workflow IDs to names and status IDs
    const triggeredByWorkflows = workflow.triggers?.map((trigger: any) => {
      const triggerWorkflow = workflows.find(w => w.workflow_id === trigger.prevWorkflowId);
      // Map programStatusMapperIds to string format for form
      const statusIds = trigger.programStatusMapperIds?.map((id: number) => id.toString()) || [];
      return {
        workflow: triggerWorkflow?.workflow_name || "",
        status: statusIds,
      };
    }).filter(t => t.workflow !== "") || [{ workflow: "", status: [] }];

    // Map task IDs to names
    const attempts = workflow.attempts?.map((attempt: any) => {
      const task = tasks.find(t => t.task_id === attempt.taskId);
      return {
        attemptNo: attempt.attempt || 1,
        tasks: task?.task_name || "",
        waitingPeriod: attempt.waitTime ?? 1, // Use ?? to preserve 0 values
        waitingPeriodUnit: "Days" as const, // API stores in days
      };
    }).filter(a => a.tasks !== "") || [{ attemptNo: 1, tasks: "", waitingPeriod: 1, waitingPeriodUnit: "Days" as const }];
    
    // Map user group ID to string
    const userGroup = workflow.userGroupId ? workflow.userGroupId.toString() : "";
    
    return {
      id: workflow.workflowId?.toString(),
      workflowName: workflow.workflowName || "",
      applicablePrograms,
      triggeredByWorkflows: triggeredByWorkflows.length > 0 ? triggeredByWorkflows : [{ workflow: "", status: [] }],
      attempts: attempts.length > 0 ? attempts : [{ attemptNo: 1, tasks: "", waitingPeriod: 1, waitingPeriodUnit: "Days" as const }],
      userGroup,
    };
  };

  useEffect(() => {
    const fetchWorkflow = async () => {
      // Wait for dropdown data to be loaded before transforming
      if (loadingPrograms || loadingWorkflows || loadingTasks) {
        return;
      }

      if (!id || !user || !token) {
        setError("Missing required parameters");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await workflowService.getWorkflowById(
          user.id.toString(),
          id,
          token
        );

        if (response.success && response.data) {
          const transformedData = transformApiDataToFormData(response.data);
          setWorkflowData(transformedData);
          setError(null);
        } else {
          setError("Failed to fetch workflow details");
          toast({
            title: "Error",
            description: "Failed to fetch workflow details",
            variant: "destructive",
          });
        }
      } catch (err: any) {
        setError(err.message || "Failed to fetch workflow");
        toast({
          title: "Error",
          description: err.message || "Failed to fetch workflow",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchWorkflow();
  }, [id, user, token, loadingPrograms, loadingWorkflows, loadingTasks, programTypes, workflows, tasks]);

  if (loading || loadingPrograms || loadingWorkflows || loadingTasks) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoaderComponent />
      </div>
    );
  }

  if (error || !workflowData) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Workflow Not Found</h1>
          <p className="text-muted-foreground mt-2">
            {error || "The requested workflow could not be found."}
          </p>
        </div>
      </div>
    );
  }

  return <WorkflowForm initialData={workflowData} isEdit />;
};

export default EditWorkflow;